import { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

interface Summary {
  days: number;
  funnel: { picks: number; creates: number; signInShown: number; signInDone: number; videoStarted: number; signedUp: number; firstBuild: number };
  latest: Array<{
    id: string;
    createdAt: string;
    words: string;
    example: string | null;
    edited: boolean;
    look: string | null;
    tier: string | null;
    consent: string;
    signInShown: boolean;
    signInDone: boolean;
    signedUp: boolean;
    email: string | null;
    projectId: string | null;
    built: boolean;
  }>;
  examples: Array<{ chip: string; picks: number; creates: number; signups: number }>;
}

const DAYS = 30;

/** Each step, and from "Saw sign-in" on, the share of the step before it that got there. */
function steps(f: Summary['funnel']): Array<[string, number, string]> {
  const rows: Array<[string, number]> = [
    ['Picked an example', f.picks],
    ['Sent words', f.creates],
    ['Saw sign-in', f.signInShown],
    ['Finished sign-in', f.signInDone],
    ['Started the video', f.videoStarted],
    ['First build', f.firstBuild],
  ];
  return rows.map(([label, n], i) => [label, n, i < 2 || !rows[i - 1][1] ? '' : `${Math.round((n / rows[i - 1][1]) * 100)}%`]);
}

/**
 * Admin: what visitors sent from the front door's landing box before signing in (beta), where they dropped off, and
 * which examples they picked. Reads GET /api/admin/front-door/summary.
 */
export function FrontDoorCapture() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get('/admin/front-door/summary', { params: { days: DAYS } }).then(
      (r) => setSummary(r.data),
      () => setFailed(true),
    );
  }, []);

  if (failed) return <p className="p-6 text-sm text-muted-foreground">Could not load the front door summary.</p>;
  if (!summary) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Front door</h1>
        <p className="text-sm text-muted-foreground">What visitors sent from the landing box before signing in, the last {summary.days} days. Rows nobody claimed are deleted after 90 days.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Drop-off per step</CardTitle></CardHeader>
          <CardContent>
            <Table aria-label="Drop-off per step">
              <TableBody>
                {steps(summary.funnel).map(([label, n, rate]) => (
                  <TableRow key={label}>
                    <TableCell>{label}</TableCell>
                    <TableCell className="text-right tabular-nums">{n}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{rate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Examples</CardTitle></CardHeader>
          <CardContent>
            <Table aria-label="Examples">
              <TableHeader>
                <TableRow><TableHead>Example</TableHead><TableHead className="text-right">Picks</TableHead><TableHead className="text-right">Sent</TableHead><TableHead className="text-right">Signed up</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {summary.examples.map((e) => (
                  <TableRow key={e.chip}>
                    <TableCell>{e.chip}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.picks}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.creates}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.signups}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Latest prompts</CardTitle></CardHeader>
        <CardContent>
          <Table aria-label="Latest prompts">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Words</TableHead>
                <TableHead>Example</TableHead>
                <TableHead>Look</TableHead>
                <TableHead>Consent</TableHead>
                <TableHead>Sign-in</TableHead>
                <TableHead>Signed up</TableHead>
                <TableHead>Video</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.latest.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(p.createdAt).toLocaleString()}</TableCell>
                  <TableCell className="max-w-md whitespace-pre-wrap">{p.words}</TableCell>
                  <TableCell>{p.example ? `${p.example}${p.edited ? ' (edited)' : ''}` : '—'}</TableCell>
                  <TableCell>{p.look ?? '—'}</TableCell>
                  <TableCell>{p.consent}</TableCell>
                  <TableCell>{p.signInDone ? 'Finished' : p.signInShown ? 'Shown' : '—'}</TableCell>
                  <TableCell>{p.signedUp ? p.email ?? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {p.projectId ? <a className="underline" href={`/chat/${p.projectId}/video-workspace`}>{p.built ? 'Built' : 'Started'}</a> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
